import { v4 as uuidv4 } from "uuid";
import { Scene } from "@babylonjs/core/scene";
import { Color4 } from "@babylonjs/core/Maths/math.color";
// 注意: スクショ実装は screenshotTools 側で提供される（tools.js 側は未import時に throw するスタブ）。
// この named import で実装本体が読み込まれる。
import { CreateScreenshotUsingRenderTargetAsync } from "@babylonjs/core/Misc/screenshotTools";
import { uploadImageFile } from "app/api";
import Constants from "common/constant";
import { dataURLToBlob } from "common/utils";
import { updatePieceCapture } from "features/builder/api";

/**
 * 現在の 3D ビルダーシーンを撮ってアップロードし、画像の URL を返す（りれきのカード・ピースのサムネイル）。
 * scene/camera が無ければ undefined。
 */
export const shootBuilderScene = async (
  scene: Scene | null | undefined,
  uid: string,
): Promise<string | undefined> => {
  if (!scene || scene.isDisposed) return undefined;
  const engine = scene.getEngine();
  const camera = scene.activeCamera;
  if (!engine || !camera) return undefined;
  // 作り直した直後は、テクスチャなどの読み込みを待ってから撮る
  await scene.whenReadyAsync();

  // 編集画面の背景（rgba(0,0,0,0.67) over white）と同じダークグレーで撮影し、撮影後に元へ戻す。
  // シーンは clearColor 透明（背景は DOM 依存）なので、透明のまま撮ると編集画面の見た目と一致しない。
  const prevClear = scene.clearColor;
  scene.clearColor = new Color4(84 / 255, 84 / 255, 84 / 255, 1); // #545454
  let dataUrl: string;
  try {
    dataUrl = await CreateScreenshotUsingRenderTargetAsync(engine, camera, {
      width: 640,
      height: 480,
    });
  } finally {
    scene.clearColor = prevClear;
  }
  const blob = dataURLToBlob(dataUrl);
  const filename = `users/${uid}/piece_capture/${uuidv4()}.png`;
  await uploadImageFile({ fileBlob: blob, filename, contentType: "image/png" });
  return `${Constants.assetHost}/${filename}`;
};

/**
 * 現在の 3D ビルダーシーンを撮ってピースの capture（サムネイル）に保存し、その URL を返す。
 * 完成時・編集終了時に呼ぶ。失敗しても致命的ではないため呼び出し側で握りつぶす想定。
 * local-map（未保存のローカル）や scene/camera 不在時は何もしない。
 */
export const captureBuilderThumbnail = async (
  scene: Scene | null | undefined,
  uid: string | undefined,
  pieceId: string | undefined,
): Promise<string | undefined> => {
  if (!uid || !pieceId || pieceId === "local-map") return undefined;
  const url = await shootBuilderScene(scene, uid);
  if (!url) return undefined;
  await updatePieceCapture({ piece_id: pieceId, uid, capture: url });
  return url;
};
